import { createStyles, makeStyles } from '@material-ui/core';
import React from 'react';

import CandidateSkillTextField from './CandidateSkillTextField';
import { EducationEntity } from './Education/EducationForm';
import EducationHistory from './Education/EducationHistory';
import { Employment } from './Employment/EmploymentForm';
import EmploymentHistory from './Employment/EmploymentHistory';
import SkillsTable, { CandidateSkill } from './SkillsTable';

interface Skill {
  name: string;
}

interface SkillsAndEducationValues {
  skillTextValue: string;
  candidateSkills: CandidateSkill[];
  employmentHistory: Employment[];
  educationHistory: EducationEntity[];
}

export interface SkillsAndEducationChangeHandlers {
  onAddSkill: (skill: Skill) => void;
  skillTextOnChange: (value: string) => void;
  onChangeSkillProficiency: (skillName: string, proficiency: number) => void;
  onChangeSkillStandout: (skillName: string, standout: boolean) => void;
  onChangeSkillVerified: (skillName: string, verified: boolean) => void;
  onDeleteSkills: (skillNames: string[]) => void;
  onAddEmployment: (employment: Employment) => void;
  onEditEmployment: (index: number, employment: Employment) => void;
  onDeleteEmployment: (index: number) => void;
  onAddEducation: (education: EducationEntity) => void;
  onEditEducation: (index: number, education: EducationEntity) => void;
  onDeleteEducation: (index: number) => void;
}

interface Props {
  values: SkillsAndEducationValues;
  changeHandlers: SkillsAndEducationChangeHandlers;
  formDisabled?: boolean;
  editFieldsDisabled?: boolean;
  onClickLearnMore?: () => void;
  onFormOpenChange?: (isOpen: boolean) => void;
  onFormValidityChange?: (isValid: boolean, invalidFields: string[]) => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      padding: '24px',
    },
    bodyContainer: {
      display: 'flex',
      flexDirection: 'column',
      flexWrap: 'wrap',
      minHeight: 250,
    },
  }),
);

export default function SkillsAndEducation({
  values,
  changeHandlers,
  formDisabled = false,
  editFieldsDisabled = false,
  onClickLearnMore,
  onFormOpenChange,
  onFormValidityChange,
}: Readonly<Props>) {
  const classes = useStyles();

  React.useEffect(() => {
    return () => onFormOpenChange?.(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={classes.container}>
      <SkillsTable
        skills={values.candidateSkills}
        changeHandlers={changeHandlers}
        onClickLearnMore={onClickLearnMore}
      />
      <div style={{ width: '50%' }}>
        <CandidateSkillTextField
          placeholder="Type to Search For Skills"
          disabled={formDisabled || editFieldsDisabled}
          name="skill"
          label="Add Candidate Skill"
          value={values.skillTextValue ?? null}
          onSelect={(selectedSkill) => {
            changeHandlers.skillTextOnChange('');
            changeHandlers.onAddSkill(selectedSkill);
          }}
          onChange={changeHandlers.skillTextOnChange}
          alreadySelectedSkillNames={values.candidateSkills.map((skill) => skill.name)}
        />
      </div>
      <EmploymentHistory
        employmentHistory={values.employmentHistory}
        onFormOpenChange={onFormOpenChange}
        onFormValidityChange={onFormValidityChange}
        changeHandlers={{
          onAddEmployment: changeHandlers.onAddEmployment,
          onEditEmployment: changeHandlers.onEditEmployment,
          onDeleteEmployment: changeHandlers.onDeleteEmployment,
        }}
      />
      <EducationHistory
        educationHistory={values.educationHistory}
        onFormOpenChange={onFormOpenChange}
        onFormValidityChange={onFormValidityChange}
        changeHandlers={{
          onAddEducation: changeHandlers.onAddEducation,
          onEditEducation: changeHandlers.onEditEducation,
          onDeleteEducation: changeHandlers.onDeleteEducation,
        }}
      />
    </div>
  );
}
